import { useEffect, useState, ReactNode } from "react"
import Searchbar from "./Searchbar"
import { Link } from "react-router-dom"
import { sortByAttribute } from "../../../utils/helper"
import { CgSortAz, CgSortZa } from "react-icons/cg"
import ItemsPerPageSelector from "./ItemsPerPageSelector"
import Pagination from "./Pagination"
import { BiPlus } from "react-icons/bi"

interface TableProps {
  data: any[]
  title: string
  currentPage?: number
  setCurrentPage?: (page: number) => void
  limit?: number
  setLimit?: (limit: number) => void
  handleSearch: (query: string) => void
  headers: { label: string; key: string; to?: string }[]
  totalCount?: number
  setTotalCount?: (count: number) => void
  to?: string
  extraHeaderContent?: ReactNode
  onRowClick?: (item: any) => void
  showAddButton?: boolean
  onAddClick?: () => void
  renderCell?: (item: any, column: { label: string; key: string; to?: string }) => ReactNode
}

export default function Table({
  data,
  title,
  currentPage,
  setCurrentPage,
  limit,
  setLimit,
  handleSearch,
  headers,
  totalCount,
  setTotalCount,
  to,
  extraHeaderContent,
  onRowClick,
  showAddButton,
  onAddClick,
  renderCell
}: TableProps) {
  const [tableData, setTableData] = useState<any[] | undefined | null>(null)
  const [acending, setAcending] = useState(true)

  useEffect(() => {
    if (data) {
      setTableData(data)
      if (setTotalCount && totalCount !== undefined) {
        setTotalCount(totalCount)
      }
    }
  }, [data, setTotalCount, totalCount])

  useEffect(() => {
    if (setCurrentPage && limit !== undefined) {
      setCurrentPage(1)
    }
  }, [limit, setCurrentPage])

  function handleSort(e: any) {
    if (!tableData) return
    if (!e.target.parentNode.name) return
    setAcending((prev) => {
      const sortedData = sortByAttribute(tableData, e.target.parentNode.name, !prev) as any[]
      setTableData(sortedData)
      return !prev
    })
  }

  function handlePageChange(pageNumber: number) {
    if (setCurrentPage) {
      setCurrentPage(pageNumber)
    }
  }

  return (
    <div className="w-[inherit]">
      <div className="flex align-middle p-2 max-w-full justify-between ">
        <div className="flex align-middle p-2 max-w-full items-center">
          <h2 className="pr-2">{title}</h2>
          {extraHeaderContent && (
            <div className="ml-4">
              {extraHeaderContent}
            </div>
          )}
          <div className="bg-secondary-extra-light shadow-md rounded-md flex justify-center items-center w-7 h-7 min-w-fit">
            <Searchbar onSearch={handleSearch} />
          </div>
          {to && (
            <Link to={to} className="w-7 h-7 ml-2 -right-7 top-3 p-1 bg-secondary-extra-light  shadow-md text-white rounded-md">
              <BiPlus color="black" size={20} />
            </Link>
          )}
          {showAddButton && onAddClick && (
            <button
              onClick={onAddClick}
              className="w-7 h-7 ml-2 -right-7 top-3 p-1 bg-secondary-extra-light shadow-md text-white rounded-md"
            >
              <BiPlus color="black" size={20} />
            </button>
          )}
        </div>
        <div className="flex items-end">
          {limit && setLimit && (
            <ItemsPerPageSelector itemsPerPage={limit} setItemsPerPage={setLimit} />
          )}
          {currentPage && setCurrentPage && totalCount && limit && (
            <Pagination
              total={totalCount}
              itemsPerPage={limit}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
      <div className="md:rounded-lg sm:overflow-auto shadow-md max-w-full">
        <table className="bg-white w-full min-w-full text-start sm:auto">
          <thead>
            <tr className="p-4" onClick={handleSort}>
              {headers.map((header, index) => (
                <th key={header.key + index} className={`px-4 py-2 bg-primary-dark text-white text-start`}>
                  {acending ? (
                    <button name={header.key} className="pl-2">
                      <span className="flex items-center">
                        {header.label}
                        <CgSortAz name={header.key} />
                      </span>
                    </button>
                  ) : (
                    <button name={header.key} className="pl-2">
                      <span className="flex items-center">
                        {header.label}
                        <CgSortZa name={header.key} />
                      </span>
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="[&>*:nth-child(odd)]:bg-secondary-extra-light pb-2 ">
            {tableData?.map((item: { [key: string]: any } | undefined) => (
              <tr
                key={item?.id + "tr"}
                onClick={() => onRowClick && item && onRowClick(item)}
                className={onRowClick ? "cursor-pointer hover:bg-gray-100" : ""}
              >
                {headers.map((header) => (
                  <td key={header.key + 1} className={`px-4 py-2 text-ellipsis overflow-hidden max-w-[150px] whitespace-nowrap`}>
                    <Link to={header.to && item?.id ? `${header.to}/${item.id}` : ""} state={{ id: item?.id }}>
                      {renderCell ? renderCell(item, header) : item && item[header.key]}
                    </Link>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
